import './style.css'
import './styles/app.css'
import { applyAppLayout, initDeviceClasses } from './ui/layout.ts'
import { initTheme } from './ui/themes.ts'
import { initUserChatFont } from './ui/fontsConfig.ts'
import { initI18n } from './i18n/index.ts'
import { getWipeOnExit } from './storage/privacySettingsStore.ts'
import { wipeVolatileOnExit } from './app/factoryReset.ts'
import { enforceMinimumClientVersion } from './app/forceUpdate.ts'
import { runSilentAutoUpdateOnLaunch } from './app/silentAutoUpdate.ts'

declare global {
  interface Window {
    __ZION_BOOTED__?: boolean
  }
}
import { bootstrapRealtime, setCurrentChatContactId, getCurrentChatContactId } from './app/zionApp.ts'
import {
  consumeDeviceLinkFromUrl,
  stashPendingDeviceLink,
  resolvePendingDeviceLink,
} from './deviceLink/deviceLinkDeepLink.ts'
import {
  hasCompletedSetup,
  isSessionUnlocked,
} from './identity/ZionIdentityManager.ts'
import { renderAddToHomeScreen } from './ui/screens/addToHomeScreen.ts'
import { renderAuthScreen } from './ui/screens/authScreen.ts'
import { renderChatScreen, clearChatScreenHooks } from './ui/screens/chatScreen.ts'
import { renderHomeScreen } from './ui/screens/homeScreen.ts'
import { renderPairScreen, clearPairScreenHooks } from './ui/screens/pairScreen.ts'
import { renderSearchScreen } from './ui/screens/searchScreen.ts'
import { renderSettingsScreen, clearSettingsScreenHooks } from './ui/screens/settingsScreen.ts'
import { renderTempChatDurationScreen } from './ui/screens/tempChatDurationScreen.ts'
import { renderTempChatScreen, clearTempChatScreenHooks } from './ui/screens/tempChatScreen.ts'
import { nextRenderGeneration } from './ui/renderGuard.ts'
import { navigate, setNavigator, getParam, type Screen } from './ui/router.ts'
import { renderLinkDeviceScreen } from './ui/screens/linkDeviceScreen.ts'
import { renderLinkDeviceScanScreen } from './ui/screens/linkDeviceScanScreen.ts'
import { registerServiceWorker } from './push/webPush.ts'
import { markBuildLoaded } from './app/appUpdate.ts'
import { ZION_BUILD } from './buildInfo.ts'
import { initAudioUnlock } from './notifications/audioUnlock.ts'
import {
  installSwInAppNotifyHooks,
  setNotifyCurrentChatGetter,
} from './notifications/messageNotify.ts'
import { initPwaUpdateBanner } from './ui/pwaUpdateBanner.ts'
import { initActiveChatVisibilityHooks, syncContactNamesToSw } from './app/activeChatBridge.ts'
import { onContactsChanged } from './storage/pairingStore.ts'
import { isCapacitorNative } from './api/ZionServer.ts'

const app = document.querySelector<HTMLDivElement>('#app')!

initAudioUnlock()
setNotifyCurrentChatGetter(() => getCurrentChatContactId())
installSwInAppNotifyHooks()
initActiveChatVisibilityHooks()
onContactsChanged(() => {
  void syncContactNamesToSw()
})
void import('./storage/contactsDbForSw.ts').then((m) => m.persistNotifyNamesFromMirror())

// PWA update banner + Service Worker: solo web/PWA — mai su Capacitor nativo
if (!isCapacitorNative()) {
  initPwaUpdateBanner()
  if ('serviceWorker' in navigator) {
    void registerServiceWorker().then((reg) => {
      if (!reg) return
      initPwaUpdateBanner(reg)
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'ZION_SW_UPDATED') {
          console.log('[ZION] SW attivo', event.data.version || '')
        }
        if (
          event.data?.type === 'ZION_PUSH_SUBSCRIPTION_CHANGED' ||
          event.data?.type === 'ZION_PUSH_NEEDS_RESYNC'
        ) {
          void import('./storage/pushStore.ts').then((m) => {
            m.markPushNeedsResubscribe()
          })
          void import('./storage/sessionStore.ts').then(async ({ getSessionToken }) => {
            const { ensurePushTokenSynced } = await import('./services/pushService.ts')
            const t = await getSessionToken()
            if (t) void ensurePushTokenSynced(t)
          })
        }
      })
    })
  }
} else {
  // Capacitor nativo: registrazione APNs automatica all'avvio (silenziosa, niente UI)
  void import('./push/pushClient.ts').then((m) => m.ensureNativePushOnLaunch())
}

document.documentElement.dataset.zionBuild = ZION_BUILD

async function renderScreen(screen: Screen): Promise<void> {
  const { isStale } = nextRenderGeneration()

  if (screen !== 'chat') {
    clearChatScreenHooks()
    setCurrentChatContactId('')
  }
  if (screen !== 'tempChat') {
    clearTempChatScreenHooks()
  }
  if (screen !== 'pair') {
    clearPairScreenHooks()
  }
  if (screen !== 'settings') {
    clearSettingsScreenHooks()
  }
  applyAppLayout(screen === 'chat' || screen === 'tempChat' ? 'chat' : 'page')
  app.classList.toggle('screen-temp-chat', screen === 'tempChat')
  switch (screen) {
    case 'auth':
      await renderAuthScreen(app)
      break
    case 'home':
      await renderHomeScreen(app, isStale)
      break
    case 'search':
      await renderSearchScreen(app)
      break
    case 'chat':
      await renderChatScreen(app)
      break
    case 'pair':
      await renderPairScreen(app)
      break
    case 'linkDevice':
      await renderLinkDeviceScreen(app)
      break
    case 'linkDeviceScan':
      await renderLinkDeviceScanScreen(app)
      break
    case 'addToHome':
      renderAddToHomeScreen(app)
      break
    case 'settings':
      await renderSettingsScreen(app)
      break
    case 'tempChatDuration':
      await renderTempChatDurationScreen(app, isStale)
      break
    case 'tempChat':
      await renderTempChatScreen(app, isStale)
      break
  }
}

let activeScreen: Screen | null = null
let activeChatContactId = ''

setNavigator((screen) => {
  const nextChatId = screen === 'chat' ? getParam('contactId') : ''
  if (screen === 'chat' && activeScreen === 'chat' && nextChatId && nextChatId === activeChatContactId) {
    return
  }
  activeScreen = screen
  activeChatContactId = nextChatId
  void renderScreen(screen)
})

async function boot(): Promise<void> {
  initDeviceClasses()
  initTheme()
  initUserChatFont()
  initI18n()
  const setupDone = await hasCompletedSetup()
  const pendingDeviceLink = consumeDeviceLinkFromUrl()
  if (pendingDeviceLink) stashPendingDeviceLink(pendingDeviceLink)

  if (!setupDone || !isSessionUnlocked()) {
    navigate('auth')
    return
  }

  try {
    await bootstrapRealtime()
  } catch {
    /* socket may retry later */
  }

  const autoLink = resolvePendingDeviceLink()
  if (autoLink) {
    navigate('linkDeviceScan', { sessionId: autoLink })
    return
  }
  navigate('home')
}

window.addEventListener('pagehide', () => {
  if (!getWipeOnExit()) return
  void wipeVolatileOnExit()
})

async function start(): Promise<void> {
  const silentReload = await runSilentAutoUpdateOnLaunch()
  if (silentReload) return

  // Segna boot SUBITO (prima di await) — evita falso "App non caricata" su reti lente
  window.__ZION_BOOTED__ = true
  markBuildLoaded()
  const blocked = await enforceMinimumClientVersion()
  if (blocked) return
  void boot()
}

void start()
